import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, AlertCircle, CheckCircle2, Award, MapPin, Calendar,
  Users, UserPlus, Upload, Download, Trash2, Search, X, Send, Save, FileText,
  ShieldCheck, Clock, Pencil, AlertTriangle,
} from "lucide-react";
import { BASE_URL } from "../../../features/api/apiSlice";
import {
  useGetChapterEventRequestQuery,
  useUpdateChapterEventRequestMutation,
  useAddChapterEventAttendeeMutation,
  useUpdateChapterEventAttendeeMutation,
  useRemoveChapterEventAttendeeMutation,
  usePreviewChapterEventAttendanceMutation,
  useImportChapterEventAttendanceMutation,
  useSubmitChapterEventAttendanceMutation,
  type ChapterEventAttendee,
  type ChapterEventRequestDetail,
  type AttendanceImportResult,
} from "../api/chapterEventsApi";
import { useSearchChapterMembersQuery, type MemberSearchResult } from "../api/chapterApi";
import ChapterEventStatusBadge from "../components/ChapterEventStatusBadge";

/**
 * One chapter event, at whatever stage it has reached.
 *
 * The same screen throughout, showing only what can be done now — which is
 * decided by the status the server sends, not re-derived here:
 *
 *   pending / rejected      the request is editable, and there is no register
 *   approved                the register is open: add members, upload a sheet,
 *                           then submit for allocation
 *   attendance_submitted    the register is read-only and waiting on the
 *                           secretariat
 *   allocated               the register with what each name earned
 *
 * That is deliberately the only place the sequence is enforced on this side. The
 * API refuses anything out of order anyway; hiding the controls is so the
 * chairman is not offered a button that will be refused.
 */

function safeMsg(e: any): string {
  const errors = e?.data?.errors;
  if (errors) return Object.values(errors).flat().join(" ");
  return e?.data?.message || e?.error || e?.message || "Something went wrong.";
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function fmtDateTime(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso.replace(" ", "T")).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function memberName(m: MemberSearchResult) {
  return [m.surname, m.firstname, m.othernames].filter(Boolean).join(" ");
}

// ── edit the request itself ──────────────────────────────────────────────────

function EditRequest({ req, onSaved }: { req: ChapterEventRequestDetail; onSaved: (m: string) => void }) {
  const [form, setForm] = useState({
    title: req.title,
    theme: req.theme ?? "",
    details: req.details ?? "",
    location: req.location,
    start_date: req.start_date,
    end_date: req.end_date,
    proposed_cpd_points: String(req.proposed_cpd_points),
  });
  const [error, setError] = useState("");
  const [save, { isLoading }] = useUpdateChapterEventRequestMutation();

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const res: any = await save({ id: req.id, body: form }).unwrap();
      onSaved(res?.message || "Request updated.");
    } catch (err) {
      setError(safeMsg(err));
    }
  };

  return (
    <form onSubmit={submit} className="border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-4">
      <div className="flex items-center gap-2">
        <FileText className="w-4 h-4 text-gray-400" />
        <h3 className="font-bold text-gray-900 text-sm">
          {req.status === "rejected" ? "Amend and re-submit" : "Edit request"}
        </h3>
      </div>

      {req.status === "rejected" && (
        // Saying so here, not only in the badge: the one thing he needs to know
        // is that saving puts it back in the queue rather than just tidying it.
        <p className="text-xs text-gray-500">
          Saving sends this back to the secretariat for a fresh decision.
        </p>
      )}

      {!!error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2 space-y-1.5">
          <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">Title</label>
          <input
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">Theme</label>
          <input
            value={form.theme}
            onChange={(e) => set("theme", e.target.value)}
            className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">Venue</label>
          <input
            value={form.location}
            onChange={(e) => set("location", e.target.value)}
            className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">Start date</label>
          <input
            type="date"
            value={form.start_date}
            onChange={(e) => set("start_date", e.target.value)}
            className="w-full border px-3 py-2.5 rounded-lg text-sm bg-white"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">End date</label>
          <input
            type="date"
            value={form.end_date}
            min={form.start_date || undefined}
            onChange={(e) => set("end_date", e.target.value)}
            className="w-full border px-3 py-2.5 rounded-lg text-sm bg-white"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">CPD points proposed</label>
          <input
            type="number"
            step="0.5"
            min="0.5"
            max="50"
            value={form.proposed_cpd_points}
            onChange={(e) => set("proposed_cpd_points", e.target.value)}
            className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
          />
        </div>
        <div className="sm:col-span-2 space-y-1.5">
          <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">Details</label>
          <textarea
            rows={4}
            value={form.details}
            onChange={(e) => set("details", e.target.value)}
            className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-40"
      >
        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {req.status === "rejected" ? "Save and re-submit" : "Save changes"}
      </button>
    </form>
  );
}

// ── add one attendee ─────────────────────────────────────────────────────────

function AddAttendee({ requestId, onDone }: { requestId: string; onDone: (m: string) => void }) {
  const [mode, setMode] = useState<"member" | "guest">("member");
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [guest, setGuest] = useState({ name: "", email: "", phone: "" });
  const [error, setError] = useState("");

  const [add, { isLoading }] = useAddChapterEventAttendeeMutation();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const { data: results, isFetching } = useSearchChapterMembersQuery(debounced, {
    skip: debounced.trim().length < 2,
  });
  const found: MemberSearchResult[] = Array.isArray(results) ? results : [];

  const pick = async (m: MemberSearchResult) => {
    setError("");
    try {
      const res: any = await add({ id: requestId, body: { user_id: Number(m.id) } }).unwrap();
      setQ(""); setDebounced("");
      onDone(res?.message || `${memberName(m)} added.`);
    } catch (e) {
      setError(safeMsg(e));
    }
  };

  const addGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const res: any = await add({
        id: requestId,
        body: {
          name: guest.name,
          email: guest.email || undefined,
          phone: guest.phone || undefined,
        },
      }).unwrap();
      setGuest({ name: "", email: "", phone: "" });
      onDone(res?.message || "Attendee added.");
    } catch (err) {
      setError(safeMsg(err));
    }
  };

  return (
    <div className="border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-4">
      <div className="flex items-center gap-2">
        <UserPlus className="w-4 h-4 text-gray-400" />
        <h3 className="font-bold text-gray-900 text-sm">Add an attendee</h3>
      </div>

      <div className="flex gap-2">
        {([
          ["member", "Member"],
          ["guest", "Not a member"],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setMode(key); setError(""); }}
            className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border ${
              mode === key
                ? "bg-gray-900 text-white border-gray-900"
                : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {!!error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {mode === "member" ? (
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email or membership no…"
              className="w-full border pl-9 pr-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
            />
          </div>

          {debounced.trim().length < 2 ? (
            <p className="text-[11px] text-gray-400 font-medium">
              Type at least two characters. Picking a member credits their own CPD record.
            </p>
          ) : isFetching ? (
            <div className="py-6 flex items-center justify-center gap-2 text-gray-400 text-xs">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          ) : found.length === 0 ? (
            <p className="text-xs text-gray-400">
              No matching member. If they are not a member, add them under “Not a member”.
            </p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {found.map((m) => (
                <button
                  key={m.id}
                  onClick={() => pick(m)}
                  disabled={isLoading}
                  className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-gray-900 transition-colors flex items-center justify-between gap-3 disabled:opacity-50"
                >
                  <div className="min-w-0">
                    <div className="font-bold text-gray-900 text-sm truncate">{memberName(m)}</div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {m.membership_no ? `${m.membership_no} • ` : ""}{m.email}
                    </div>
                  </div>
                  <UserPlus className="w-4 h-4 text-gray-300 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={addGuest} className="space-y-3">
          {/* Stated up front rather than discovered at allocation. */}
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-2">
            A non-member is recorded on the register and earns no CPD — there is no
            member record to credit.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              required
              value={guest.name}
              onChange={(e) => setGuest((g) => ({ ...g, name: e.target.value }))}
              placeholder="Full name *"
              className="border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
            />
            <input
              type="email"
              value={guest.email}
              onChange={(e) => setGuest((g) => ({ ...g, email: e.target.value }))}
              placeholder="Email"
              className="border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
            />
            <input
              value={guest.phone}
              onChange={(e) => setGuest((g) => ({ ...g, phone: e.target.value }))}
              placeholder="Phone"
              className="border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-40"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Add attendee
          </button>
        </form>
      )}
    </div>
  );
}

// ── correct one attendee ─────────────────────────────────────────────────────

/**
 * Fix a row: name, email, phone, membership number — or pick the member outright.
 *
 * This is how an attendee nobody matched becomes creditable, so it stays open long
 * after the rest of the register has closed: anything not yet credited can be
 * corrected, including after the CPD has been allocated. The secretariat then
 * allocates again and only the newly matched are paid.
 */
function EditAttendeeModal({
  requestId,
  attendee,
  onClose,
  onSaved,
}: {
  requestId: string;
  attendee: ChapterEventAttendee;
  onClose: () => void;
  onSaved: (m: string) => void;
}) {
  const [form, setForm] = useState({
    name: attendee.name ?? "",
    email: attendee.email ?? "",
    phone: attendee.phone ?? "",
    membership_no: attendee.membership_no ?? "",
  });
  const [picked, setPicked] = useState<MemberSearchResult | null>(null);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [error, setError] = useState("");

  const [save, { isLoading }] = useUpdateChapterEventAttendeeMutation();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const { data: results, isFetching } = useSearchChapterMembersQuery(debounced, {
    skip: debounced.trim().length < 2,
  });
  const found: MemberSearchResult[] = Array.isArray(results) ? results : [];

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const res: any = await save({
        id: requestId,
        attendeeId: attendee.id,
        body: {
          name: form.name,
          email: form.email || null,
          phone: form.phone || null,
          // Only sent when the chairman picked somebody — otherwise the typed
          // number is what re-runs the match, and sending both would have the
          // picker silently win over a number he had just corrected.
          ...(picked
            ? { user_id: Number(picked.id) }
            : { membership_no: form.membership_no || null }),
        },
      }).unwrap();
      onSaved(res?.message || "Attendee updated.");
    } catch (err) {
      setError(safeMsg(err));
    }
  };

  const detach = async () => {
    setError("");
    try {
      const res: any = await save({
        id: requestId,
        attendeeId: attendee.id,
        body: { user_id: null, membership_no: null },
      }).unwrap();
      onSaved(res?.message || "Attendee detached from that member.");
    } catch (err) {
      setError(safeMsg(err));
    }
  };

  return (
    <div className="fixed inset-0 z-[210] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <form
        onSubmit={submit}
        className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[94vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-bold text-gray-900">Correct attendee</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {attendee.needs_match
                ? "No member matched, so this row earns no CPD yet."
                : "Matched to a member and awaiting allocation."}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!!error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 space-y-1.5">
              <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">Name</label>
              <input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">Phone</label>
              <input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
              />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">
                Membership number
              </label>
              <input
                value={form.membership_no}
                disabled={!!picked}
                onChange={(e) => { set("membership_no", e.target.value); }}
                placeholder="20356, P00214, M00871…"
                className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              />
              <p className="text-[10px] text-gray-400">
                Changing this re-runs the match. Any series the member is known by
                will do; padding and prefixes can be written either way.
              </p>
            </div>
          </div>

          {/* Surer than a typed number, so it is offered alongside it. */}
          <div className="border rounded-xl p-3 space-y-2.5">
            <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
              Or pick the member
            </div>

            {picked ? (
              <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-green-50 border border-green-200">
                <div className="min-w-0">
                  <div className="font-bold text-green-900 text-sm truncate">{memberName(picked)}</div>
                  <div className="text-[11px] text-green-700 truncate">
                    {picked.membership_no ? `${picked.membership_no} • ` : ""}{picked.email}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPicked(null)}
                  className="text-[10px] font-bold uppercase tracking-wide text-green-800 underline shrink-0"
                >
                  Undo
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name, email or membership no…"
                    className="w-full border pl-8 pr-3 py-2 rounded-lg text-xs outline-none focus:border-blue-500"
                  />
                </div>

                {debounced.trim().length >= 2 && (
                  isFetching ? (
                    <div className="py-4 flex items-center justify-center gap-2 text-gray-400 text-xs">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching…
                    </div>
                  ) : found.length === 0 ? (
                    <p className="text-[11px] text-gray-400">No matching member.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {found.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => { setPicked(m); setQ(""); setDebounced(""); }}
                          className="w-full text-left p-2.5 rounded-lg border border-gray-100 hover:border-gray-900 transition-colors"
                        >
                          <div className="font-semibold text-gray-900 text-xs truncate">{memberName(m)}</div>
                          <div className="text-[10px] text-gray-500 truncate">
                            {m.membership_no ? `${m.membership_no} • ` : ""}{m.email}
                          </div>
                        </button>
                      ))}
                    </div>
                  )
                )}
              </>
            )}

            {!attendee.needs_match && !picked && (
              // A row matched to the WRONG member: detaching puts it back to being
              // a plain name on the register, which can then be matched again.
              <button
                type="button"
                onClick={detach}
                disabled={isLoading}
                className="text-[10px] font-bold uppercase tracking-wide text-red-600 underline disabled:opacity-40"
              >
                Wrong member — detach this row
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 border rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 py-2.5 bg-gray-900 rounded-lg text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

// ── spreadsheet upload ───────────────────────────────────────────────────────

/**
 * What the sheet should look like, shown rather than described.
 *
 * The template is a download, and a chairman filling one in on a phone or
 * working from a sheet somebody else prepared will not open it. These are the
 * same three example rows the template carries, with the reason for each spelled
 * out — the three cases the parser actually distinguishes.
 */
const SAMPLE_ROWS: { cells: [string, string, string, string]; note: string; tone: string }[] = [
  {
    cells: ["20356", "", "", ""],
    note: "A member on the plain series. Name, email and phone are read off their record.",
    tone: "text-green-700",
  },
  {
    cells: ["P00214", "OKAFOR Chidi", "", "08040000000"],
    note: "A probationer — their own series, and they earn CPD like anybody else.",
    tone: "text-green-700",
  },
  {
    cells: ["M00871", "", "", ""],
    note: "The prefixed form the older part of the register carries.",
    tone: "text-green-700",
  },
  {
    cells: ["", "ADEYEMI Tolu", "tolu@example.com", "08030000000"],
    note: "No number — matched on the email their portal account uses.",
    tone: "text-green-700",
  },
  {
    cells: ["", "BELLO Aisha (guest)", "", "08050000000"],
    note: "Nobody the register recognises. Kept on the attendance, earns no CPD, flagged so you can correct it.",
    tone: "text-amber-700",
  },
];

function SampleSheet() {
  const [open, setOpen] = useState(true);

  return (
    <div className="border rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 text-left"
      >
        <span className="text-[10px] font-black uppercase tracking-widest text-gray-500">
          What the sheet should look like
        </span>
        <span className="text-[10px] font-bold text-gray-400">{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        <div className="p-3 space-y-3">
          <div className="overflow-x-auto">
            <table className="w-full text-[11px] border-collapse">
              <thead>
                <tr className="bg-gray-100 text-gray-600">
                  {["membership_no", "name", "email", "phone"].map((h) => (
                    <th key={h} className="border px-2 py-1.5 font-bold text-left whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SAMPLE_ROWS.map((r, i) => (
                  <tr key={i}>
                    {r.cells.map((c, j) => (
                      <td key={j} className="border px-2 py-1.5 text-gray-700 whitespace-nowrap">
                        {c || <span className="text-gray-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="space-y-1">
            {SAMPLE_ROWS.map((r, i) => (
              <li key={i} className={`text-[10px] ${r.tone}`}>
                <span className="font-bold">Row {i + 2}:</span> {r.note}
              </li>
            ))}
          </ul>

          <p className="text-[10px] text-gray-400 leading-relaxed">
            Headings go in row 1, in any order. Columns we do not recognise are
            ignored, so your own extra columns are safe to leave in. Any series the
            member is known by will do — <span className="font-semibold">20356</span>,{" "}
            <span className="font-semibold">P00214</span>,{" "}
            <span className="font-semibold">M00871</span>,{" "}
            <span className="font-semibold">F/06970</span> — and the zero padding and
            prefix can be written either way.
          </p>
          <p className="text-[10px] text-gray-400 leading-relaxed">
            <span className="font-bold text-gray-500">Fill in the name on every row.</span>{" "}
            A number that matches nobody does not throw the row away — the attendee
            is still recorded, flagged, and you can correct the number afterwards,
            even once the CPD has been allocated. Without a name there is nothing to
            record and the row is skipped.
          </p>
        </div>
      )}
    </div>
  );
}

function UploadAttendance({
  requestId,
  onClose,
  onImported,
}: {
  requestId: string;
  onClose: () => void;
  onImported: (m: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<AttendanceImportResult | null>(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const [preview, { isLoading: previewing }] = usePreviewChapterEventAttendanceMutation();
  const [commit, { isLoading: importing }] = useImportChapterEventAttendanceMutation();

  const body = () => {
    const fd = new FormData();
    if (file) fd.append("file", file);
    return fd;
  };

  const runPreview = async () => {
    setError("");
    try {
      const res: any = await preview({ id: requestId, body: body() }).unwrap();
      setResult(res?.data ?? res);
    } catch (e: any) {
      setError(safeMsg(e));
      // A refusal can still carry the row-by-row verdict; showing it beats a
      // one-line error the chairman cannot act on.
      setResult(e?.data?.data ?? null);
    }
  };

  const runImport = async () => {
    setError("");
    try {
      const res: any = await commit({ id: requestId, body: body() }).unwrap();
      setResult(res?.data ?? res);
      onImported(res?.message || "Attendance added.");
    } catch (e: any) {
      setError(safeMsg(e));
      setResult(e?.data?.data ?? null);
    }
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    setError("");
    try {
      // RTK Query parses JSON, so the workbook is fetched by hand with the token
      // attached — the same way the event registration template downloads.
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${BASE_URL}/chapter/event-requests/${requestId}/attendees/import/template`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "chapter-event-attendance-template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message ?? "Could not download the template.");
    } finally {
      setDownloading(false);
    }
  };

  const rows = result?.rows ?? [];
  const imported = Number(result?.imported ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[94vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-bold text-gray-900">Upload Attendance</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Checked first, row by row. Nothing is saved until you confirm.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          <button
            onClick={downloadTemplate}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 text-[10px] font-black uppercase tracking-widest hover:bg-indigo-100 disabled:opacity-60"
          >
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Download template
          </button>

          <SampleSheet />

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-gray-600 uppercase tracking-tight">
              Attendance sheet (.xlsx or .csv)
            </label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv,.txt"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setError(""); }}
              className="w-full border px-3 py-2.5 rounded-lg text-sm bg-white file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-gray-900 file:text-white file:text-xs file:font-semibold"
            />
          </div>

          {!!error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!!result && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  ["Rows", result.total_rows, "text-gray-900"],
                  ["Will earn CPD", result.members, "text-green-700"],
                  ["Unmatched", result.unmatched, "text-amber-700"],
                  ["Bad number", result.flagged, "text-amber-700"],
                  ["Skipped", result.invalid, "text-red-600"],
                ].map(([label, value, tone]) => (
                  <div key={String(label)} className="border rounded-xl p-3">
                    <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</div>
                    <div className={`text-lg font-bold ${tone}`}>{String(value)}</div>
                  </div>
                ))}
              </div>

              {result.unmatched > 0 && (
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                  {result.unmatched} row(s) match no member and earn no CPD.
                  {result.flagged > 0 && (
                    <>
                      {" "}
                      <span className="font-bold">{result.flagged}</span> of those carry a
                      number that resolved to nobody.
                    </>
                  )}{" "}
                  They are still added to the register, so nobody goes missing from the
                  attendance — correct them from the register afterwards and the CPD can
                  be allocated to them then.
                </p>
              )}

              <div className="border rounded-xl overflow-hidden">
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left text-gray-500">
                        <th className="px-3 py-2 font-bold">Row</th>
                        <th className="px-3 py-2 font-bold">Name</th>
                        <th className="px-3 py-2 font-bold">Member no</th>
                        <th className="px-3 py-2 font-bold">Verdict</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr
                          key={r.line}
                          className={`border-t ${
                            r.status === "error"
                              ? "bg-red-50/60"
                              : r.status === "warning"
                                ? "bg-amber-50/60"
                                : ""
                          }`}
                        >
                          <td className="px-3 py-2 text-gray-400">{r.line}</td>
                          <td className="px-3 py-2 font-semibold text-gray-800">{r.name || "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{r.membership_no || "—"}</td>
                          <td className="px-3 py-2">
                            {r.status === "error" ? (
                              // Nothing to record — the only case a row is dropped.
                              <span className="text-red-600">Skipped: {r.errors.join(" ")}</span>
                            ) : r.status === "warning" ? (
                              <span className="text-amber-800">
                                <span className="font-semibold">Added without CPD.</span>{" "}
                                {r.warnings.join(" ")}
                              </span>
                            ) : r.type === "member" ? (
                              <span className="text-green-700 font-semibold">Member — will earn CPD</span>
                            ) : (
                              <span className="text-amber-700 font-semibold">
                                No number given — added without CPD
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 px-5 sm:px-6 py-4 border-t sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50"
          >
            {imported ? "Done" : "Cancel"}
          </button>
          <button
            onClick={runPreview}
            disabled={!file || previewing || importing}
            className="flex-1 py-2.5 border border-gray-900 rounded-lg text-sm font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Check file
          </button>
          <button
            onClick={runImport}
            disabled={!file || !result || result.valid === 0 || importing || previewing || imported}
            className="flex-1 py-2.5 bg-gray-900 rounded-lg text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {result ? `Add ${result.valid} attendee(s)` : "Add attendance"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function ChapterEventDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const { data: req, isLoading, error: loadError } = useGetChapterEventRequestQuery(id, { skip: !id });

  const [removeAttendee, { isLoading: removing }] = useRemoveChapterEventAttendeeMutation();
  const [submitAttendance, { isLoading: submitting }] = useSubmitChapterEventAttendanceMutation();

  const [ok, setOk] = useState("");
  const [error, setError] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState<ChapterEventAttendee | null>(null);

  // Memoised, not just `req?.attendees ?? []`: the fallback is a fresh array on
  // every render, which would make the filter below re-run each time.
  const attendees: ChapterEventAttendee[] = useMemo(() => req?.attendees ?? [], [req]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return attendees;
    return attendees.filter((a) =>
      [a.name, a.membership_no, a.email].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [attendees, filter]);

  const memberCount = attendees.filter((a) => a.is_member).length;
  const unmatchedCount = attendees.filter((a) => a.needs_match).length;
  // Whether the Correct column is worth a place at all. It outlives the open
  // register: an uncredited row stays editable after the CPD has been allocated,
  // which is the whole point of the flag.
  const anyEditable = attendees.some((a) => a.can_edit);

  const drop = async (a: ChapterEventAttendee) => {
    if (!window.confirm(`Remove ${a.name} from the register?`)) return;
    setError(""); setOk("");
    try {
      const res: any = await removeAttendee({ id, attendeeId: a.id }).unwrap();
      setOk(res?.message || "Attendee removed.");
    } catch (e) {
      setError(safeMsg(e));
    }
  };

  const submit = async () => {
    if (
      !window.confirm(
        `Submit ${attendees.length} attendee(s) for CPD allocation? The register closes and cannot be changed afterwards.`,
      )
    ) return;
    setError(""); setOk("");
    try {
      const res: any = await submitAttendance(id).unwrap();
      setOk(res?.message || "Submitted for CPD allocation.");
    } catch (e) {
      setError(safeMsg(e));
    }
  };

  if (isLoading) {
    return (
      <div className="p-10 flex flex-col items-center gap-2 text-gray-400 bg-white rounded-lg shadow-sm">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-xs font-medium">Loading event…</span>
      </div>
    );
  }

  if (loadError || !req) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-sm space-y-4">
        <div className="px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm font-semibold text-red-700">
          {(loadError as any)?.data?.message || "This event request could not be loaded."}
        </div>
        <button onClick={() => navigate("/chapter/events")} className="text-sm font-semibold text-gray-600 underline">
          Back to chapter events
        </button>
      </div>
    );
  }

  const registerOpen = !!req.can_upload_attendance;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 bg-white rounded-lg shadow-sm">
      <Link
        to="/chapter/events"
        className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-900 uppercase tracking-wide"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Chapter events
      </Link>

      {/* ── header ─────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">{req.title}</h2>
          <ChapterEventStatusBadge status={req.status} />
        </div>
        {!!req.theme && <p className="text-sm text-gray-500 italic">{req.theme}</p>}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {req.location}</span>
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {fmtDate(req.start_date)}{req.end_date && req.end_date !== req.start_date ? ` – ${fmtDate(req.end_date)}` : ""}
          </span>
          {!!req.chapter && <span className="font-semibold text-gray-600">{req.chapter.name}</span>}
        </div>
      </div>

      {!!ok && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-green-200 bg-green-50 text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{ok}</span>
        </div>
      )}
      {!!error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ── CPD standing ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border rounded-2xl p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">CPD proposed</div>
          <div className="text-2xl font-bold text-gray-900">{req.proposed_cpd_points}</div>
        </div>
        <div className="border rounded-2xl p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">CPD approved</div>
          <div className={`text-2xl font-bold ${req.cpd_was_reduced ? "text-amber-600" : "text-gray-900"}`}>
            {req.approved_cpd_points ?? "—"}
          </div>
          {req.cpd_was_reduced && (
            <div className="text-[10px] font-bold text-amber-600 uppercase tracking-wide mt-0.5">
              Reduced from {req.proposed_cpd_points}
            </div>
          )}
        </div>
        <div className="border rounded-2xl p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">On register</div>
          <div className="text-2xl font-bold text-gray-900">{attendees.length}</div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mt-0.5">
            {memberCount} member{memberCount === 1 ? "" : "s"}
            {unmatchedCount > 0 ? ` · ${unmatchedCount} unmatched` : ""}
          </div>
        </div>
      </div>

      {!!req.review_notes && (
        <div
          className={`px-3 py-2.5 rounded-lg border text-sm ${
            req.status === "rejected"
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-blue-200 bg-blue-50 text-blue-800"
          }`}
        >
          <span className="font-bold">
            {req.status === "rejected" ? "Rejected" : "Secretariat note"}
            {req.reviewed_by ? ` — ${req.reviewed_by}` : ""}:
          </span>{" "}
          {req.review_notes}
        </div>
      )}

      {!!req.details && (
        <div className="border rounded-2xl p-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Details submitted</div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{req.details}</p>
        </div>
      )}

      {/* ── stage-specific ─────────────────────────────────────────────── */}

      {req.status === "pending" && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">
          <Clock className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Waiting on the secretariat. You can amend the request until it has been
            reviewed; attendance can only be uploaded once it is approved.
          </span>
        </div>
      )}

      {req.can_edit && <EditRequest req={req} onSaved={(m) => { setOk(m); setError(""); }} />}

      {req.status === "attendance_submitted" && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-indigo-200 bg-indigo-50 text-sm text-indigo-800">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Submitted for CPD allocation on {fmtDateTime(req.attendance_submitted_at)}. The
            register is now closed; the secretariat allocates the CPD from here.
          </span>
        </div>
      )}

      {req.status === "allocated" && !!req.allocation && (
        <div className="border-2 border-green-200 bg-green-50 rounded-2xl p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-green-700" />
            <h3 className="font-bold text-green-800 text-sm">CPD allocated</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-green-700/70">Points each</div>
              <div className="font-bold text-green-900">{req.allocation.points_each}</div>
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-green-700/70">Members credited</div>
              <div className="font-bold text-green-900">{req.allocation.credited}</div>
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-green-700/70">Not credited</div>
              <div className="font-bold text-green-900">{req.allocation.not_credited}</div>
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-green-700/70">Total points issued</div>
              <div className="font-bold text-green-900">{req.allocation.total_points}</div>
            </div>
          </div>
          <p className="text-[11px] text-green-800/80">
            Allocated {fmtDateTime(req.allocated_at)}
            {req.allocated_by ? ` by ${req.allocated_by}` : ""}. It is now a chapter event on
            every attendee's own CPD record.
          </p>
        </div>
      )}

      {/* The one thing still to do after an allocation, and the reason an
          uncredited row stays editable. Shown at both stages the rows can be in. */}
      {unmatchedCount > 0 && (req.status === "allocated" || req.status === "attendance_submitted") && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border-2 border-amber-300 bg-amber-50 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <span className="font-bold">
              {unmatchedCount} attendee{unmatchedCount === 1 ? "" : "s"} matched no member
            </span>{" "}
            and {req.status === "allocated" ? "were not credited" : "will not be credited"}.
            Correct the membership number on those rows below — or pick the member from
            the search — and the secretariat can allocate again to credit just them.
            Rows already credited are not touched.
          </span>
        </div>
      )}

      {registerOpen && (
        <>
          <AddAttendee requestId={id} onDone={(m) => { setOk(m); setError(""); }} />

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => { setShowUpload(true); setOk(""); setError(""); }}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-900 text-gray-900 text-sm font-semibold hover:bg-gray-50"
            >
              <Upload className="w-4 h-4" /> Upload attendance sheet
            </button>
            <button
              onClick={submit}
              disabled={submitting || attendees.length === 0}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-40"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit for CPD allocation
            </button>
          </div>
        </>
      )}

      {/* ── the register ───────────────────────────────────────────────── */}
      {(attendees.length > 0 || registerOpen) && (
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h3 className="font-bold text-gray-900 text-sm inline-flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" /> Attendance register
              <span className="text-gray-400 font-medium">({attendees.length})</span>
            </h3>
            {attendees.length > 5 && (
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter the register…"
                  className="border pl-8 pr-3 py-2 rounded-lg text-xs outline-none focus:border-blue-500"
                />
              </div>
            )}
          </div>

          {attendees.length === 0 ? (
            <div className="py-12 text-center space-y-1 border-2 border-dashed border-gray-200 rounded-2xl">
              <p className="text-sm font-bold text-gray-500">The register is empty</p>
              <p className="text-xs text-gray-400">
                Add members one at a time above, or upload the chapter's attendance sheet.
              </p>
            </div>
          ) : (
            <div className="border rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                      <th className="px-3 py-2.5">Name</th>
                      <th className="px-3 py-2.5">Member no</th>
                      <th className="px-3 py-2.5 hidden sm:table-cell">Email</th>
                      <th className="px-3 py-2.5 hidden md:table-cell">Phone</th>
                      <th className="px-3 py-2.5">CPD</th>
                      {/* Shown whenever ANY row can still be corrected — which
                          outlives the open register, because an uncredited row
                          stays editable after allocation. */}
                      {anyEditable && <th className="px-3 py-2.5 text-right">Correct</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((a) => (
                      <tr
                        key={a.id}
                        // Unmatched rows are tinted, not merely badged: after
                        // allocation they are the only thing left to act on and
                        // they must stand out in a list of two hundred names.
                        className={`border-t ${a.needs_match ? "bg-amber-50/60" : ""}`}
                      >
                        <td className="px-3 py-2.5">
                          <div className="font-semibold text-gray-900">{a.name}</div>
                          {a.needs_match && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 mt-0.5">
                              <AlertTriangle className="w-3 h-3" /> No member matched
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-gray-500">{a.membership_no || "—"}</td>
                        <td className="px-3 py-2.5 text-gray-500 hidden sm:table-cell">{a.email || "—"}</td>
                        <td className="px-3 py-2.5 text-gray-500 hidden md:table-cell">{a.phone || "—"}</td>
                        <td className="px-3 py-2.5">
                          {a.cpd_issued ? (
                            <span className="inline-flex items-center gap-1 text-green-700 font-bold text-xs">
                              <Award className="w-3.5 h-3.5" /> {a.cpd_points}
                            </span>
                          ) : a.needs_match ? (
                            <span className="text-xs font-semibold text-amber-700">
                              {req.status === "allocated" ? "Not credited — fix the number" : "Earns nothing yet"}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">
                              {req.status === "allocated" ? "Awaiting allocation" : "Pending allocation"}
                            </span>
                          )}
                        </td>
                        {anyEditable && (
                          <td className="px-3 py-2.5">
                            <div className="flex items-center justify-end gap-1">
                              {a.can_edit ? (
                                <>
                                  <button
                                    onClick={() => setEditing(a)}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100"
                                    title="Correct name, contact or membership number"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => drop(a)}
                                    disabled={removing}
                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                                    title="Remove from the register"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                // Credited: its points are on a ledger entry and
                                // its name on a registration, so there is nothing
                                // here to change.
                                <span className="text-[10px] font-bold uppercase tracking-wide text-gray-300">
                                  Credited
                                </span>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {showUpload && (
        <UploadAttendance
          requestId={id}
          onClose={() => setShowUpload(false)}
          onImported={(m) => { setOk(m); setError(""); }}
        />
      )}

      {!!editing && (
        <EditAttendeeModal
          requestId={id}
          attendee={editing}
          onClose={() => setEditing(null)}
          onSaved={(m) => { setEditing(null); setOk(m); setError(""); }}
        />
      )}
    </div>
  );
}
